import React, { useState, useEffect, useMemo } from 'react';
import { createClient } from '../services/api';
import { buildApiUrl } from '../config/api';
import { getApiAccessToken } from '../config/authToken';
import {
  FullScreenModal,
  FullScreenModalContent,
  FullScreenModalHeader,
  FullScreenModalTitle,
  FullScreenModalBackButton,
  FullScreenModalBody,
} from './ui/fullscreen-modal';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Search, Plus, UserPlus, Loader2, User } from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { generateUniqueRandomNickname } from '../utils/uniqueNickname';
import { useI18n } from '../i18n/context';

const ClientSelectorModal = ({ 
  open, 
  onOpenChange, 
  selectedClientIds = [], 
  onSelect,
  trainerId = null,
  singleSelect = false
}) => {
  const { t } = useI18n();
  const [clients, setClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState(selectedClientIds);
  const [isLoading, setIsLoading] = useState(false);
  
  // Состояние для инлайн-создания клиента
  const [isCreating, setIsCreating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newClient, setNewClient] = useState({
    name: '',
    comment: '',
  });
  const [errors, setErrors] = useState({});

  // Получаем trainerId из пропсов или из localStorage как fallback
  const getEffectiveTrainerId = () => {
    if (trainerId) {
      return trainerId;
    }
    
    // Fallback: получаем из localStorage
    try {
      const savedUser = localStorage.getItem('currentUser');
      if (savedUser) {
        const user = JSON.parse(savedUser);
        return user.id;
      }
    } catch (e) {
      console.error('Error getting user from localStorage:', e);
    }
    return null;
  };

  const effectiveTrainerId = getEffectiveTrainerId();

  // Загрузка клиентов для тренера
  useEffect(() => {
    if (open) {
      loadClients();
      setSelectedIds(selectedClientIds);
      setSearchQuery('');
      setIsCreating(false);
      resetNewClientForm();
    }
  }, [open, selectedClientIds, effectiveTrainerId]);

  const resetNewClientForm = () => {
    setNewClient({
      name: '',
      comment: '',
    });
    setErrors({});
  };

  const loadClients = async () => {
    const trainerIdToUse = effectiveTrainerId;
    
    if (!trainerIdToUse) {
      console.warn('[ClientSelectorModal] No trainerId available, returning empty');
      setClients([]);
      setIsLoading(false);
      return;
    }
    
    try {
      setIsLoading(true);
      const opts = { credentials: 'include' };
      const bearer = getApiAccessToken();
      if (bearer) opts.headers = { Authorization: `Bearer ${bearer}` };
      const clientsUrl = buildApiUrl(`/users/${trainerIdToUse}/clients`);
      const clientsResponse = await fetch(clientsUrl, opts);
      if (!clientsResponse.ok) {
        throw new Error('Failed to fetch clients');
      }
      const clientsData = await clientsResponse.json();
      setClients(clientsData || []);
    } catch (err) {
      console.error('[ClientSelectorModal] Ошибка загрузки клиентов:', err);
      toast.error(t('clients.errorLoad'));
      setClients([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Фильтрация клиентов по поисковому запросу
  const filteredClients = useMemo(() => {
    if (!searchQuery.trim()) {
      return clients;
    }
    const query = searchQuery.toLowerCase().trim();
    return clients.filter(client => {
      const nickname = (client.nickname || '').toLowerCase();
      const name = (client.name || '').toLowerCase();
      return nickname.includes(query) || name.includes(query);
    });
  }, [clients, searchQuery]);

  // Обработка выбора клиента
  const handleSelectClient = (clientId) => {
    if (singleSelect) {
      onSelect([clientId]);
      onOpenChange(false);
    } else {
      setSelectedIds(prev => {
        if (prev.includes(clientId)) {
          return prev.filter(id => id !== clientId);
        } else {
          return [...prev, clientId];
        }
      });
    }
  };

  // Обработка подтверждения выбора (для мульти-режима)
  const handleDone = () => {
    onSelect(selectedIds);
    onOpenChange(false);
  };

  // Обработка отмены
  const handleCancel = () => {
    setSelectedIds(selectedClientIds);
    onOpenChange(false);
  };

  const handleNameChange = (value) => {
    setNewClient((prev) => ({ ...prev, name: value }));
    if (errors.name) {
      setErrors((prev) => ({ ...prev, name: undefined }));
    }
  };

  // Валидация формы
  const validateForm = () => {
    const newErrors = {};
    const trimmed = newClient.name.trim();

    if (!trimmed) {
      newErrors.name = t('clients.validationName');
    } else if (
      clients.some(
        (c) =>
          (c.name || '').trim() &&
          (c.name || '').trim().toLowerCase() === trimmed.toLowerCase()
      )
    ) {
      newErrors.name = t('clients.duplicateName');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Создание нового клиента
  const handleCreateClient = async (e) => {
    e.preventDefault();

    if (isSubmitting) {
      return;
    }
    
    if (!validateForm()) {
      return;
    }

    // Validate that trainer_id is available
    if (!effectiveTrainerId) {
      toast.error(t('clients.errorTrainerId'));
      console.error('[ClientSelectorModal] Cannot create client: trainer_id is missing');
      return;
    }

    try {
      setIsSubmitting(true);
      let nickname;
      try {
        nickname = await generateUniqueRandomNickname();
      } catch (genErr) {
        console.error(genErr);
        toast.error(genErr?.message || t('clients.errorNickname'));
        setIsSubmitting(false);
        return;
      }

      const response = await createClient({
        nickname,
        name: newClient.name.trim(),
        comment: newClient.comment && newClient.comment.trim() ? newClient.comment.trim() : null,
        trainer_id: effectiveTrainerId,
      });
      
      const createdClient = response.data;
      toast.success(
        t('clients.successAdded', { name: createdClient.name || createdClient.nickname || '' })
      );
      
      // Перезагружаем список клиентов, чтобы новый клиент появился в списке
      try {
        await loadClients();
      } catch (loadErr) {
        console.error('[ClientSelectorModal] Error reloading clients list:', loadErr);
        // Don't fail the whole operation if reload fails
      }
      
      // Сразу выбираем созданного клиента и закрываем модал
      onSelect([createdClient.id]);
      onOpenChange(false);
    } catch (err) {
      console.error('[ClientSelectorModal] Ошибка создания клиента:', err);
      console.error('[ClientSelectorModal] Error details:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        message: err.message
      });
      const code = err.response?.data?.code;
      const errorMessage =
        code === 'DUPLICATE_CLIENT_NAME'
          ? t('clients.duplicateName')
          : err.response?.data?.error || t('clients.createFailedShort');
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasClients = clients.length > 0;
  const hasFilteredClients = filteredClients.length > 0;

  // Форма создания клиента
  const renderCreateForm = (isCompact = false) => (
    <form onSubmit={handleCreateClient} className={cn(
      "space-y-4",
      isCompact ? "p-4 rounded-lg border border-primary/30 bg-primary/5" : "w-full max-w-sm"
    )}>
      <div className="space-y-1.5">
        <Label htmlFor="clientName" className="text-sm font-medium">
          {t('clients.nameLabel')}
        </Label>
        <Input
          id="clientName"
          type="text"
          placeholder={t('clients.enterNamePlaceholder')}
          value={newClient.name}
          onChange={(e) => handleNameChange(e.target.value)}
          className={cn(
            'bg-white placeholder:text-muted-foreground',
            errors.name && 'border-red-500'
          )}
          autoFocus
        />
        {errors.name && <p className="text-xs text-red-500">{errors.name}</p>}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="clientComment" className="text-sm font-medium">{t('clients.commentLabel')}</Label>
        <Textarea
          id="clientComment"
          placeholder={t('clients.enterCommentPlaceholder')}
          value={newClient.comment}
          onChange={(e) => setNewClient((prev) => ({ ...prev, comment: e.target.value }))}
          className="bg-white min-h-[80px] placeholder:text-muted-foreground"
          rows={3}
        />
      </div>

      {/* Кнопки */}
      <div className={cn("flex gap-2", isCompact ? "" : "pt-2")}>
        {isCompact && (
          <Button 
            type="button" 
            variant="outline"
            onClick={() => {
              setIsCreating(false);
              resetNewClientForm();
            }}
            className="flex-1"
          >
            {t('clients.cancel')}
          </Button>
        )}
        <Button 
          type="submit" 
          className={isCompact ? "flex-1" : "w-full"}
          disabled={isSubmitting || !newClient.name.trim()}
        >
          {isSubmitting ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <UserPlus className="h-4 w-4 mr-2" />
          )}
          {t('clients.addClient')}
        </Button>
      </div>
    </form>
  );

  return (
    <>
      <FullScreenModal open={open} onOpenChange={onOpenChange}>
      <FullScreenModalContent className="flex flex-col">
        {/* Header */}
        <FullScreenModalHeader>
          <FullScreenModalBackButton onClick={handleCancel} />
          <FullScreenModalTitle>
            {singleSelect ? t('clients.pickerTitleSingle') : t('clients.pickerTitleMulti')}
          </FullScreenModalTitle>
          {!singleSelect && (
            <Button
              variant="ghost"
              onClick={handleDone}
              className="text-primary font-medium -mr-2 min-w-[60px]"
            >
              {t('clients.pickerDone')}
            </Button>
          )}
          {singleSelect && <div className="w-[60px]" />}
        </FullScreenModalHeader>

        {/* Body */}
        <FullScreenModalBody>
          <div className="space-y-4">
            {/* Поиск - показываем только если есть клиенты */}
            {hasClients && !isCreating && (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder={t('clients.pickerSearch')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 bg-white"
                  autoFocus
                />
              </div>
            )}

            {/* Загрузка */}
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : !hasClients ? (
              <div className="flex flex-col items-center justify-center py-6 text-center">
                <User className="h-16 w-16 text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {t('clients.emptyTitle')}
                </h3>
                <p className="text-sm text-muted-foreground mb-6 max-w-xs">
                  {t('clients.pickerFirstSubtitle')}
                </p>
                {renderCreateForm(false)}
              </div>
            ) : (
              /* Список клиентов */
              <div className="space-y-1">
                {!isCreating && (
                  <>
                    {hasFilteredClients ? (
                      filteredClients.map((client) => {
                        const isSelected = selectedIds.includes(client.id);
                        const displayName = (client.name && String(client.name).trim())
                          ? String(client.name).trim()
                          : t('clients.unnamed');
                        
                        return (
                          <div
                            key={client.id}
                            onClick={() => handleSelectClient(client.id)}
                            className={cn(
                              "flex items-center gap-3 p-3 rounded-lg cursor-pointer touch-manipulation transition-colors",
                              "hover:bg-accent active:bg-accent/80",
                              isSelected && "bg-primary/10 border border-primary/30"
                            )}
                          >
                            <User className="h-5 w-5 text-primary flex-shrink-0" />
                            <span className={cn(
                              "flex-1 text-base",
                              isSelected && "font-medium text-primary"
                            )}>
                              {displayName}
                            </span>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        {t('clients.pickerNotFound')}
                      </div>
                    )}

                    {/* Кнопка добавления нового клиента - всегда видна */}
                    <div
                      onClick={() => setIsCreating(true)}
                      className={cn(
                        "flex items-center justify-center gap-2 p-4 rounded-xl cursor-pointer touch-manipulation transition-all mt-6",
                        "bg-primary/5 border-2 border-dashed border-primary/30 hover:bg-primary/10 hover:border-primary/50 active:bg-primary/15"
                      )}
                    >
                      <Plus className="h-5 w-5 text-primary flex-shrink-0" />
                      <span className="text-base font-medium text-primary">
                        {t('clients.pickerAddNew')}
                      </span>
                    </div>
                  </>
                )}

                {/* Инлайн форма создания */}
                {isCreating && renderCreateForm(true)}
              </div>
            )}
          </div>
        </FullScreenModalBody>
      </FullScreenModalContent>
    </FullScreenModal>
    </>
  );
};

export default ClientSelectorModal;
